import {
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useDispatch, useSelector } from "react-redux";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const FONT = "0.8rem";

const NAT_AVG = { vehicle: 23.4, driver: 6.7, hazmat: 4.4 };
const REF_MILEAGE = 20_000;

export const CLASS_OPTIONS = [
  { label: "Contractor fleet", eligible: true, limitOverride: null },
  { label: "Other light/service fleet", eligible: true, limitOverride: null },
  { label: "Second wave auto", eligible: true, limitOverride: null },
  {
    label: "Concrete mixed in transit",
    eligible: true,
    limitOverride: 5_000_000,
  },
  { label: "Construction debris", eligible: true, limitOverride: 2_000_000 },
  { label: "For-hire trucking", eligible: false, limitOverride: null },
  { label: "Forestry/logging", eligible: false, limitOverride: null },
  { label: "Waste hauling", eligible: false, limitOverride: null },
  { label: "Sand/gravel hauling", eligible: false, limitOverride: null },
  { label: "Taxi/Limi/Bus", eligible: false, limitOverride: null },
  { label: "Auto rental", eligible: false, limitOverride: null },
  { label: "HNOA food delivery", eligible: false, limitOverride: null },
  { label: "Drive-away contractor", eligible: false, limitOverride: null },
  { label: "Medical transport", eligible: false, limitOverride: null },
];

// Score → limit/attachment guidance. Sourced from Excel "Auto Limit-Attachment
// Guide" range T1:X6 — VLOOKUP(score, ..., col, TRUE) (approximate match, so
// each threshold is the SMALLEST score that picks that row).
const SCORE_TIERS = [
  {
    minIncl: 0.0,
    minAtt: 1_000_000,
    maxLead: 5_000_000,
    max5m: 5_000_000,
    max25m: 10_000_000,
  },
  {
    minIncl: 1.5,
    minAtt: 1_000_000,
    maxLead: 3_000_000,
    max5m: 5_000_000,
    max25m: 10_000_000,
  },
  {
    minIncl: 2.0,
    minAtt: 1_000_000,
    maxLead: 2_000_000,
    max5m: 5_000_000,
    max25m: 5_000_000,
  },
  {
    minIncl: 2.25,
    minAtt: 2_000_000,
    maxLead: 3_000_000,
    max5m: 5_000_000,
    max25m: 5_000_000,
  },
  {
    minIncl: 2.5,
    minAtt: 5_000_000,
    maxLead: null,
    max5m: 5_000_000,
    max25m: 5_000_000,
  },
  {
    minIncl: 3.0,
    minAtt: 5_000_000,
    maxLead: null,
    max5m: null,
    max25m: 5_000_000,
  },
];

// Power-unit-count → tier (Excel O1:P11). Approximate VLOOKUP: returns the
// tier whose threshold is the largest value ≤ totalUnits.
const UNIT_TIERS = [
  [0, 1],
  [25, 2],
  [50, 3],
  [75, 4],
  [100, 5],
  [150, 6],
  [200, 7],
  [250, 8],
  [500, 9],
  [750, 10],
  [1000, 11],
];
// MAX(vehicle/driver/hazmat OOS ratio) → tier (Excel O18:P25).
const OOS_TIERS = [
  [0.0, 0],
  [0.75, 0],
  [0.9, 0],
  [1.1, 1],
  [1.25, 2],
  [1.35, 3],
  [1.5, 4],
  [2.0, 5],
];
// Heavy% (heavy power units / total) → tier (Excel O27:P32).
const HEAVY_PCT_TIERS = [
  [0.0, 0],
  [0.1, 1],
  [0.25, 2],
  [0.35, 3],
  [0.5, 4],
  [0.75, 5],
];
// State → score (Low=1, Medium=2, High=3). Sourced from lookups!S3:Z53 col 8
// (state→tier) and lookups!O30:P32 (tier→score).
const STATE_HAZARD_TIER = {
  AK: "Medium",
  AL: "Medium",
  AR: "Low",
  AZ: "Medium",
  CA: "High",
  CO: "Medium",
  CT: "Medium",
  DC: "Medium",
  DE: "Medium",
  FL: "High",
  GA: "High",
  HI: "Low",
  IA: "Low",
  ID: "Low",
  IL: "Medium",
  IN: "Low",
  KS: "Low",
  KY: "Medium",
  LA: "High",
  MA: "Medium",
  MD: "Medium",
  ME: "Low",
  MI: "Low",
  MN: "Low",
  MO: "Medium",
  MS: "Medium",
  MT: "Low",
  NC: "Medium",
  ND: "Low",
  NE: "Low",
  NH: "Low",
  NJ: "High",
  NM: "Medium",
  NV: "High",
  NY: "High",
  OH: "Low",
  OK: "Medium",
  OR: "Low",
  PA: "Medium",
  RI: "Low",
  SC: "High",
  SD: "Low",
  TN: "Low",
  TX: "High",
  UT: "Medium",
  VA: "Low",
  VT: "Low",
  WA: "Low",
  WI: "Low",
  WV: "Low",
  WY: "Low",
};
const USAGE_HAZARD_TIER = {
  "Local/Service": "Low",
  "Local/Commercial": "Medium",
  "Local/Retail": "Medium",
  "Intermediate/Service": "Medium",
  "Intermediate/Commercial": "High",
  "Intermediate/Retail": "High",
  "Long-Haul": "High",
};
const SETTING_HAZARD_TIER = { Rural: "Low", Average: "Medium", Urban: "High" };
const TIER_SCORE = { Low: 1, Medium: 2, High: 3 };

// Ascending lookup: returns tier-value for the LARGEST threshold ≤ value (Excel
// VLOOKUP with the `TRUE` flag). If value < first threshold, returns first tier.
function approxVlookup(table, value) {
  let match = table[0][1];
  for (const [min, tier] of table) {
    if (value >= min) match = tier;
    else break;
  }
  return match;
}

export function computeLimits({
  totalUnits,
  heavyUnits,
  state,
  vehicleUsage,
  setting,
  vehicleOos,
  driverOos,
  hazmatOos,
  classOption,
}) {
  if (!classOption?.eligible) return null;

  const total = totalUnits || 0;
  const heavy = Math.min(heavyUnits || 0, total);
  const heavyPct = total > 0 ? heavy / total : 0;

  const unitsTier = approxVlookup(UNIT_TIERS, total);
  const heavyPctTier = approxVlookup(HEAVY_PCT_TIERS, heavyPct);

  const vRatio = NAT_AVG.vehicle > 0 ? (vehicleOos || 0) / NAT_AVG.vehicle : 0;
  const dRatio = NAT_AVG.driver > 0 ? (driverOos || 0) / NAT_AVG.driver : 0;
  const hRatio = NAT_AVG.hazmat > 0 ? (hazmatOos || 0) / NAT_AVG.hazmat : 0;
  const oosTier = approxVlookup(OOS_TIERS, Math.max(vRatio, dRatio, hRatio));

  const stateScore = TIER_SCORE[STATE_HAZARD_TIER[state]] ?? 0;
  const usageScore = TIER_SCORE[USAGE_HAZARD_TIER[vehicleUsage]] ?? 0;
  const settingScore = TIER_SCORE[SETTING_HAZARD_TIER[setting]] ?? 0;

  // Composite score — Excel cells R11/R13/R15/R17/R25/R32 summed at R33.
  const score =
    unitsTier * 0.25 +
    stateScore * 0.25 +
    usageScore * 0.075 +
    settingScore * 0.075 +
    oosTier * 0.1 +
    heavyPctTier * 0.25;

  // VLOOKUP score → limits row (largest threshold ≤ score wins).
  let tier = SCORE_TIERS[0];
  for (const t of SCORE_TIERS) {
    if (score >= t.minIncl) tier = t;
    else break;
  }

  // Class-of-business override: per Excel, a class-level cap (lookups!AL column,
  // non-null) bumps the Minimum Attachment up to that value (more conservative).
  // Other rows unchanged. A "Decline" class is already short-circuited above.
  if (classOption.limitOverride != null) {
    return { ...tier, minAtt: classOption.limitOverride };
  }
  return tier;
}

const fmtMoney = (v) =>
  v == null ? "Decline" : `$${(v / 1_000_000).toFixed(0)}M`;
const fmtPct = (v) => (v == null ? "—" : `${v.toFixed(2)}%`);

const hSx = { fontSize: "0.75rem", fontWeight: 700, py: 0.75, px: 1 };
const cSx = { fontSize: FONT, py: 0.5, px: 1 };
const numSx = {
  "& .MuiInputBase-input": {
    fontSize: FONT,
    textAlign: "right",
    py: "4px",
    px: 1,
  },
};

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const SubTitle = ({ children }) => (
  <Typography fontSize={"0.75rem"} fontWeight={600} color={"text.secondary"}>
    {children}
  </Typography>
);

const AutoLimitGuideTab = () => {
  const autoExp = useSelector(programSelectors.getAutoExposureRating);
  const xsRating = useSelector(programSelectors.getXsRating);

  // Derived from Auto Exposure Rating
  const state = autoExp?.predominantState ?? "—";
  const totalUnits =
    (autoExp?.vehiclePpt ?? 0) +
    (autoExp?.vehicleLightTruck ?? 0) +
    (autoExp?.vehicleMediumTruck ?? 0) +
    (autoExp?.vehicleHeavyTruck ?? 0) +
    (autoExp?.vehicleExtraHeavyTruck ?? 0) +
    (autoExp?.vehicleHeavyTruckTractor ?? 0) +
    (autoExp?.vehicleExtraHeavyTruckTractor ?? 0) +
    (autoExp?.vehiclePassengerTransport ?? 0) +
    (autoExp?.vehicleOther ?? 0);
  const heavyUnits =
    (autoExp?.vehicleHeavyTruck ?? 0) +
    (autoExp?.vehicleExtraHeavyTruck ?? 0) +
    (autoExp?.vehicleHeavyTruckTractor ?? 0) +
    (autoExp?.vehicleExtraHeavyTruckTractor ?? 0) +
    (autoExp?.vehiclePassengerTransport ?? 0);
  // Excel "Auto Limit-Attachment Guide" cell B5 shows the GUIDELINE auto hazard
  // (`='Auto Exposure Rating'!B24`), not the user's XS Rating override.
  const autoHazard =
    xsRating?.autoHazardGuideline ?? xsRating?.autoHazard ?? "Medium";

  // Persist the Auto Limit Guide inputs through Redux so they survive tab
  // switches (the tab unmounts when the user clicks XS Rating / Details).
  const dispatch = useDispatch();
  const persistedClassLabel = xsRating?.autoLimitClassLabel ?? "";
  const persistedVehicleOos = xsRating?.autoLimitVehicleOos ?? null;
  const persistedDriverOos = xsRating?.autoLimitDriverOos ?? null;
  const persistedHazmatOos = xsRating?.autoLimitHazmatOos ?? null;
  const persistedTotalMileage = xsRating?.autoLimitTotalMileage ?? null;

  const setClassLabel = (v) =>
    dispatch(programActions.updateXsRating("autoLimitClassLabel", v));
  const setVehicleOos = (v) =>
    dispatch(programActions.updateXsRating("autoLimitVehicleOos", v));
  const setDriverOos = (v) =>
    dispatch(programActions.updateXsRating("autoLimitDriverOos", v));
  const setHazmatOos = (v) =>
    dispatch(programActions.updateXsRating("autoLimitHazmatOos", v));
  const setTotalMileage = (v) =>
    dispatch(programActions.updateXsRating("autoLimitTotalMileage", v));

  const classLabel = persistedClassLabel;
  const vehicleOos = persistedVehicleOos;
  const driverOos = persistedDriverOos;
  const hazmatOos = persistedHazmatOos;
  const totalMileage = persistedTotalMileage;

  const classOption = CLASS_OPTIONS.find((c) => c.label === classLabel) ?? null;

  const avgMileage =
    totalMileage != null && totalUnits > 0
      ? totalMileage / totalUnits
      : totalMileage ?? 0;

  const ratios = {
    vehicle: vehicleOos != null ? (vehicleOos / NAT_AVG.vehicle) * 100 : null,
    driver: driverOos != null ? (driverOos / NAT_AVG.driver) * 100 : null,
    hazmat: hazmatOos != null ? (hazmatOos / NAT_AVG.hazmat) * 100 : null,
    mileage: totalMileage != null ? (avgMileage / REF_MILEAGE) * 100 : null,
  };

  const limits = computeLimits({
    totalUnits,
    heavyUnits,
    state: autoExp?.predominantState,
    vehicleUsage: autoExp?.vehicleUsage,
    setting: autoExp?.setting,
    vehicleOos: vehicleOos ?? 0,
    driverOos: driverOos ?? 0,
    hazmatOos: hazmatOos ?? 0,
    classOption,
  });

  const isDeclined = classOption != null && !classOption.eligible;

  return (
    <Stack spacing={2.5} sx={{ pr: 2 }}>
      <Typography
        fontSize={FONT}
        color={"text.secondary"}
        sx={{ fontStyle: "italic" }}
      >
        {
          "Guidance applies to the auto portion of Excess only and does not contemplate GL hazards. Limits and attachments are guides only and should be considered along with individual risk characteristics."
        }
      </Typography>

      {/* ── Risk Inputs ── */}
      <Stack spacing={1.5}>
        <SubTitle>{"Risk Inputs"}</SubTitle>

        {/* Top inputs */}
        <Table size={"small"}>
          <TableBody>
            <TableRow hover>
              <TableCell sx={{ ...cSx, width: 200 }}>
                {"Class of Business"}
              </TableCell>
              <TableCell sx={cSx} colSpan={3}>
                <TextField
                  {...TF}
                  select
                  sx={numSx}
                  value={classLabel}
                  onChange={(e) => setClassLabel(e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem
                    value={""}
                    sx={{ fontSize: FONT, color: "text.secondary" }}
                  >
                    {"— Select —"}
                  </MenuItem>
                  {CLASS_OPTIONS.map((c) => (
                    <MenuItem
                      key={c.label}
                      value={c.label}
                      sx={{
                        fontSize: FONT,
                        color: c.eligible ? "inherit" : "error.main",
                      }}
                    >
                      {c.label}
                      {!c.eligible && "  (Decline)"}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
            </TableRow>
            {[
              { label: "State", value: state },
              { label: "Total Power Units", value: totalUnits || "—" },
              { label: "Heavy+ Power Units", value: heavyUnits || 0 },
              { label: "Auto Hazard", value: autoHazard },
            ].map(({ label, value }) => (
              <TableRow key={label} hover>
                <TableCell sx={cSx}>{label}</TableCell>
                <TableCell
                  sx={{ ...cSx, color: "text.secondary" }}
                  colSpan={3}
                  align={"right"}
                >
                  {value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* SAFER OOS + Mileage */}
        <Table size={"small"} sx={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 160 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 90 }} />
          </colgroup>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={hSx}>{"SAFER OOS Rates"}</TableCell>
              <TableCell sx={hSx} align={"right"}>
                {"OOS Rate (%)"}
              </TableCell>
              <TableCell sx={hSx} align={"right"}>
                {"National Avg"}
              </TableCell>
              <TableCell sx={hSx} align={"right"}>
                {"Ratio"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              {
                key: "vehicle",
                label: "Vehicle",
                natAvg: NAT_AVG.vehicle,
                val: vehicleOos,
                set: setVehicleOos,
                ratio: ratios.vehicle,
              },
              {
                key: "driver",
                label: "Driver",
                natAvg: NAT_AVG.driver,
                val: driverOos,
                set: setDriverOos,
                ratio: ratios.driver,
              },
              {
                key: "hazmat",
                label: "Hazmat",
                natAvg: NAT_AVG.hazmat,
                val: hazmatOos,
                set: setHazmatOos,
                ratio: ratios.hazmat,
              },
            ].map(({ key, label, natAvg, val, set, ratio }) => (
              <TableRow key={key} hover>
                <TableCell sx={cSx}>{label}</TableCell>
                <TableCell sx={cSx} align={"right"}>
                  <PrettyNumberTextField
                    {...TF}
                    sx={numSx}
                    value={val}
                    onChangeNumber={set}
                    suffix={"%"}
                  />
                </TableCell>
                <TableCell
                  sx={{ ...cSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {fmtPct(natAvg)}
                </TableCell>
                <TableCell
                  sx={{
                    ...cSx,
                    color:
                      ratio != null && ratio > 100
                        ? "warning.main"
                        : "text.secondary",
                  }}
                  align={"right"}
                >
                  {fmtPct(ratio)}
                </TableCell>
              </TableRow>
            ))}

            {/* Mileage rows */}
            <TableRow hover>
              <TableCell
                sx={{ ...cSx, borderTop: "1px solid", borderColor: "divider" }}
              >
                {"Mileage (MCS 150)"}
              </TableCell>
              <TableCell
                sx={{ ...cSx, borderTop: "1px solid", borderColor: "divider" }}
                align={"right"}
              >
                <PrettyNumberTextField
                  {...TF}
                  sx={numSx}
                  value={totalMileage}
                  onChangeNumber={setTotalMileage}
                />
              </TableCell>
              <TableCell
                sx={{ borderTop: "1px solid", borderColor: "divider" }}
              />
              <TableCell
                sx={{ borderTop: "1px solid", borderColor: "divider" }}
              />
            </TableRow>
            <TableRow hover>
              <TableCell sx={cSx}>{"Average Mileage"}</TableCell>
              <TableCell sx={{ ...cSx }} align={"right"}>
                {totalMileage != null
                  ? Math.round(avgMileage).toLocaleString()
                  : "—"}
              </TableCell>
              <TableCell
                sx={{ ...cSx, color: "text.secondary" }}
                align={"right"}
              >
                {REF_MILEAGE.toLocaleString()}
              </TableCell>
              <TableCell
                sx={{
                  ...cSx,
                  color:
                    ratios.mileage != null && ratios.mileage > 100
                      ? "warning.main"
                      : "text.secondary",
                }}
                align={"right"}
              >
                {fmtPct(ratios.mileage)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>

      {/* ── Limit / Attachment Guidance ── */}
      <Table size={"small"} sx={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: 160 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 100 }} />
          <col style={{ width: 90 }} />
        </colgroup>
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell sx={hSx}>{"Limit / Attachment Guidance"}</TableCell>
            <TableCell colSpan={2} />
            <TableCell sx={hSx} align={"right"}>
              {"Guidance"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            { label: "Minimum Attachment", value: limits?.minAtt },
            { label: "Max Lead Limit", value: limits?.maxLead },
            { label: "Max Limit × $5M Att", value: limits?.max5m },
            { label: "Max Limit × $25M Att", value: limits?.max25m },
          ].map(({ label, value }) => {
            const declined = isDeclined || (limits != null && value == null);
            const display = isDeclined
              ? "Decline"
              : limits == null
              ? "—"
              : fmtMoney(value);
            return (
              <TableRow key={label} hover>
                <TableCell sx={cSx}>{label}</TableCell>
                <TableCell colSpan={2} />
                <TableCell
                  sx={{
                    ...cSx,
                    fontWeight: 700,
                    color: declined ? "error.main" : "text.primary",
                  }}
                  align={"right"}
                >
                  {display}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
};

export default AutoLimitGuideTab;
